// Column and table definitions for the registry. Plain erasable TypeScript so
// Node can import it directly (scripts/gen-pgtap-schema.mjs).

export type Kind = 'uuid' | 'text' | 'int' | 'bigint' | 'bool' | 'date' | 'timestamptz' | 'json'

/** A JSON value as stored in a `json` column (jsonb on the server, TEXT locally). */
export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

/** Postgres type name for each kind (as information_schema / pgTAP report it). */
export const PG_TYPE: Record<Kind, string> = {
  uuid: 'uuid',
  text: 'text',
  int: 'integer',
  bigint: 'bigint',
  bool: 'boolean',
  date: 'date',
  timestamptz: 'timestamp with time zone',
  json: 'jsonb',
}

export interface Column<
  K extends Kind = Kind,
  N extends boolean = boolean,
  E extends string = string,
  S extends boolean = boolean,
> {
  readonly kind: K
  readonly nullable: N
  /** Allowed values (from the table's CHECK constraint), for zod and types. */
  readonly values: readonly E[] | null
  /** Postgres has a default, so writers may omit it. */
  readonly hasDefault: boolean
  /** Set by the server (sync_push/triggers); clients never write it. */
  readonly server: S
  /** Postgres type override when it differs from the kind's default (e.g. smallint). */
  readonly pgType: string | null
}

export interface ColumnBuilder<
  K extends Kind,
  N extends boolean,
  E extends string,
  S extends boolean,
> extends Column<K, N, E, S> {
  orNull(): ColumnBuilder<K, true, E, S>
  oneOf<V extends string>(...values: V[]): ColumnBuilder<K, N, V, S>
  withDefault(): ColumnBuilder<K, N, E, S>
  serverSet(): ColumnBuilder<K, N, E, true>
  pg(type: string): ColumnBuilder<K, N, E, S>
}

function builder<K extends Kind, N extends boolean, E extends string, S extends boolean>(
  col: Column<K, N, E, S>,
): ColumnBuilder<K, N, E, S> {
  return {
    ...col,
    orNull: () => builder<K, true, E, S>({ ...col, nullable: true }),
    oneOf: <V extends string>(...values: V[]) => builder<K, N, V, S>({ ...col, values }),
    withDefault: () => builder({ ...col, hasDefault: true }),
    serverSet: () => builder<K, N, E, true>({ ...col, server: true }),
    pg: (type: string) => builder({ ...col, pgType: type }),
  }
}

function column<K extends Kind>(kind: K): ColumnBuilder<K, false, string, false> {
  return builder<K, false, string, false>({
    kind,
    nullable: false,
    values: null,
    hasDefault: false,
    server: false,
    pgType: null,
  })
}

export const c = {
  uuid: () => column('uuid'),
  text: () => column('text'),
  int: () => column('int'),
  bigint: () => column('bigint'),
  bool: () => column('bool'),
  date: () => column('date'),
  timestamptz: () => column('timestamptz'),
  /** Display-only or settings data; never use a JSON field in business rules. */
  json: () => column('json'),
}

export type Columns = Record<string, Column>

export interface Table<Name extends string = string, Cols extends Columns = Columns> {
  readonly name: Name
  readonly columns: Cols
}

/** Columns every synced table has (see the master-data migration header). */
export const syncColumns = {
  id: c.uuid(),
  tenant_id: c.uuid(),
  created_at: c.timestamptz().withDefault(),
  updated_at: c.timestamptz().withDefault(),
  deleted_at: c.timestamptz().orNull(),
  server_updated_at: c.timestamptz().withDefault().serverSet(),
}

export function defineTable<Name extends string, Cols extends Columns>(
  name: Name,
  columns: Cols,
): Table<Name, typeof syncColumns & Cols> {
  return { name, columns: { ...syncColumns, ...columns } }
}

// -----------------------------------------------------------------------------
// Type-level row inference
// -----------------------------------------------------------------------------

type BaseValue<K extends Kind, E extends string> = K extends 'bool'
  ? boolean
  : K extends 'json'
    ? JsonValue
    : K extends 'int' | 'bigint'
      ? number
      : string extends E
        ? string
        : E

export type ValueOf<C> =
  C extends Column<infer K, infer N, infer E, boolean>
    ? BaseValue<K, E> | (N extends true ? null : never)
    : never

type Simplify<T> = { [K in keyof T]: T[K] } & {}

type ServerKeys<Cols> = {
  [K in keyof Cols]: Cols[K] extends Column<Kind, boolean, string, true> ? K : never
}[keyof Cols]

/** A row as stored locally / pulled from the server. Server-set columns may be absent before sync. */
export type Row<T extends Table> = Simplify<
  { [K in Exclude<keyof T['columns'], ServerKeys<T['columns']>>]: ValueOf<T['columns'][K]> } & {
    [K in ServerKeys<T['columns']>]?: ValueOf<T['columns'][K]> | null
  }
>

/** A row as clients write it (server-set columns omitted). */
export type WritableRow<T extends Table> = Simplify<{
  [K in Exclude<keyof T['columns'], ServerKeys<T['columns']>>]: ValueOf<T['columns'][K]>
}>
